import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Message {
    id: string;
    text: string;
    isUser: boolean;
    timestamp: Date;
}

const AIChat: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<Message[]>([
        {
            id: '1',
            text: 'مرحباً! أنا مساعدك السياحي الذكي. كيف يمكنني مساعدتك اليوم؟',
            isUser: false,
            timestamp: new Date()
        }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    // AI responses for tourism questions
    const getAIResponse = (question: string): string => {
        const lowerQuestion = question.toLowerCase();
        
        // Greetings
        if (lowerQuestion.includes('مرحبا') || lowerQuestion.includes('السلام') || lowerQuestion.includes('hello') || lowerQuestion.includes('hi')) {
            return 'مرحباً وأهلاً وسهلاً! أنا مساعدك السياحي الذكي. كيف يمكنني مساعدتك في التخطيط لرحلتك القادمة؟ 🌟';
        }
        
        // Hotels
        if (lowerQuestion.includes('فندق') || lowerQuestion.includes('hotel') || lowerQuestion.includes('إقامة') || lowerQuestion.includes('منتجع')) {
            return 'لدينا مجموعة مميزة من الفنادق والمنتجعات! يمكنني مساعدتك في العثور على الإقامة المثالية. أخبرني:\n• المدينة أو البلد المطلوب\n• عدد النجوم المفضل\n• الميزانية التقريبية\n• تاريخ الوصول والمغادرة 🏨';
        }
        
        // Prices
        if (lowerQuestion.includes('سعر') || lowerQuestion.includes('price') || lowerQuestion.includes('تكلفة') || lowerQuestion.includes('كم') || lowerQuestion.includes('cost')) {
            return 'الأسعار تتنوع حسب عدة عوامل:\n• الموسم (ذروة أم هادئ)\n• نوع الإقامة والخدمات\n• وسيلة النقل\n• مدة الرحلة\n\nيمكنني مساعدتك في العثور على أفضل العروض! ما هي وجهتك المفضلة؟ 💰';
        }
        
        // Best destinations
        if (lowerQuestion.includes('أفضل') || lowerQuestion.includes('best') || lowerQuestion.includes('مميز') || lowerQuestion.includes('وجهة') || lowerQuestion.includes('destination')) {
            return 'إليك أفضل الوجهات السياحية حسب الاهتمامات:\n\n🏙️ **للحضارة والتسوق**: دبي، نيويورك، طوكيو\n🏛️ **للتاريخ والثقافة**: روما، باريس، القاهرة\n🏖️ **للاسترخاء**: المالديف، بالي، سانتوريني\n🗻 **للمغامرة**: سويسرا، نيوزيلندا، النرويج\n\nما نوع الرحلة التي تفضلها؟';
        }
        
        // Visa
        if (lowerQuestion.includes('تأشيرة') || lowerQuestion.includes('visa') || lowerQuestion.includes('جواز')) {
            return 'متطلبات التأشيرة تختلف حسب:\n• جنسيتك وجواز سفرك\n• البلد المقصود\n• مدة الإقامة\n• غرض الزيارة\n\nأخبرني من أي بلد أنت وإلى أين تريد السفر، وسأعطيك معلومات دقيقة عن التأشيرة المطلوبة! 📋';
        }
        
        // Weather
        if (lowerQuestion.includes('طقس') || lowerQuestion.includes('weather') || lowerQuestion.includes('مناخ') || lowerQuestion.includes('أفضل وقت')) {
            return 'التوقيت المناسب للسفر مهم جداً! إليك دليل سريع:\n\n🌞 **الربيع** (مارس-مايو): مثالي لأوروبا واليابان\n☀️ **الصيف** (يونيو-أغسطس): رائع لشمال أوروبا وكندا\n🍂 **الخريف** (سبتمبر-نوفمبر): مميز للهند والشرق الأوسط\n❄️ **الشتاء** (ديسمبر-فبراير): أفضل لجنوب شرق آسيا والخليج\n\nما هي وجهتك المفضلة؟';
        }
        
        // Food & Restaurants
        if (lowerQuestion.includes('مطعم') || lowerQuestion.includes('restaurant') || lowerQuestion.includes('طعام') || lowerQuestion.includes('food') || lowerQuestion.includes('أكل')) {
            return 'الطعام جزء لا يتجزأ من التجربة السياحية! 🍽️\n\nأشهر المأكولات العالمية:\n• **إيطاليا**: البيتزا والباستا الأصلية\n• **اليابان**: السوشي والرامن\n• **الهند**: الكاري والتوابل\n• **فرنسا**: المعجنات والجبن\n• **تايلاند**: الطعام الحار والحلو\n\nأي نوع مطبخ تفضل؟';
        }
        
        // Transportation
        if (lowerQuestion.includes('نقل') || lowerQuestion.includes('transport') || lowerQuestion.includes('مواصلات') || lowerQuestion.includes('طيران') || lowerQuestion.includes('flight')) {
            return 'خيارات النقل متنوعة حسب المدينة:\n\n✈️ **للمسافات الطويلة**: الطيران\n🚄 **داخل أوروبا**: القطارات السريعة\n🚇 **داخل المدن**: المترو والحافلات\n🚗 **للمرونة**: تأجير السيارات\n🚕 **للراحة**: التاكسي وأوبر\n\nما هي المدينة التي تخطط لزيارتها؟';
        }
        
        // Thanks
        if (lowerQuestion.includes('شكراً') || lowerQuestion.includes('شكرا') || lowerQuestion.includes('thank')) {
            return 'العفو! 😊 سعيد جداً أن أساعدك في التخطيط لرحلتك. إذا كان لديك أي أسئلة أخرى حول السفر والسياحة، لا تتردد في السؤال!';
        }
        
        // How are you
        if (lowerQuestion.includes('كيف حالك') || lowerQuestion.includes('شلونك') || lowerQuestion.includes('how are you')) {
            return 'الحمد لله، أنا بخير وجاهز لمساعدتك! 😄 أحب مساعدة المسافرين في التخطيط لرحلاتهم المميزة. كيف يمكنني مساعدتك اليوم؟';
        }
        
        // Countries/Cities specific questions
        if (lowerQuestion.includes('دبي') || lowerQuestion.includes('dubai')) {
            return 'دبي وجهة رائعة! 🏙️ إليك أهم معالمها:\n• برج خليفة (أطول برج في العالم)\n• نافورة دبي الراقصة\n• دبي مول للتسوق\n• جزيرة النخلة\n• سفاري الصحراء\n• شاطئ جميرا\n\nأفضل وقت للزيارة: نوفمبر - مارس. هل تريد معلومات عن الفنادق؟';
        }
        
        if (lowerQuestion.includes('باريس') || lowerQuestion.includes('paris')) {
            return 'باريس مدينة الحب والنور! 🗼 المعالم الأساسية:\n• برج إيفل\n• متحف اللوفر\n• كاتدرائية نوتردام\n• الشانزليزيه\n• قوس النصر\n• ديزني لاند باريس\n\nأفضل وقت للزيارة: أبريل-يونيو، سبتمبر-أكتوبر. تحتاج تأشيرة شنغن للدخول.';
        }
        
        // Help
        if (lowerQuestion.includes('مساعدة') || lowerQuestion.includes('help')) {
            return 'بالطبع! يمكنني مساعدتك في:\n\n📍 اختيار الوجهات السياحية\n🏨 حجز الفنادق والمنتجعات\n✈️ معلومات الطيران والنقل\n📋 متطلبات التأشيرة\n🌤️ أفضل أوقات السفر\n🍽️ نصائح المطاعم والطعام\n💰 مقارنة الأسعار والعروض\n\nما الذي تحتاج مساعدة فيه تحديداً؟';
        }
        
        // Default response - more helpful
        return 'شكراً لسؤالك! 🤔 أنا هنا لمساعدتك في جميع أمور السفر والسياحة.\n\nيمكنك أن تسألني عن:\n• أفضل الوجهات السياحية\n• أسعار الفنادق والرحلات\n• متطلبات التأشيرة\n• أفضل أوقات السفر\n• المطاعم والطعام\n• وسائل النقل\n\nما الذي تريد معرفته تحديداً؟ 😊';
    };

    const handleSendMessage = async () => {
        if (!inputValue.trim()) return;

        const userMessage: Message = {
            id: Date.now().toString(),
            text: inputValue,
            isUser: true,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        setIsTyping(true);

        // Simulate AI thinking time
        await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 1000));

        const aiResponse = getAIResponse(inputValue);
        const aiMessage: Message = {
            id: (Date.now() + 1).toString(),
            text: aiResponse,
            isUser: false,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, aiMessage]);
        setIsTyping(false);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const toggleChat = () => {
        setIsOpen(!isOpen);
    };

    return (
        <>
            {/* Floating Chat Button */}
            <motion.button
                className="ai-chat-button"
                onClick={toggleChat}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 2, type: "spring", stiffness: 200 }}
            >
                <span className="ai-icon">🤖</span>
                <span className="ai-label">اسألني</span>
            </motion.button>

            {/* Chat Widget */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        className="ai-chat-widget"
                        initial={{ opacity: 0, scale: 0.8, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.8, y: 20 }}
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    >
                        {/* Chat Header */}
                        <div className="chat-header">
                            <div className="chat-title">
                                <span className="ai-avatar">🤖</span>
                                <div>
                                    <h3>المساعد السياحي الذكي</h3>
                                    <span className="status">متصل الآن</span>
                                </div>
                            </div>
                            <button 
                                className="close-button"
                                onClick={toggleChat}
                            >
                                ✕
                            </button>
                        </div>

                        {/* Chat Messages */}
                        <div className="chat-messages">
                            {messages.map((message) => (
                                <motion.div
                                    key={message.id}
                                    className={`message ${message.isUser ? 'user' : 'ai'}`}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <div className="message-content">
                                        {message.text}
                                    </div>
                                    <div className="message-time">
                                        {message.timestamp.toLocaleTimeString('ar-SA', { 
                                            hour: '2-digit', 
                                            minute: '2-digit' 
                                        })}
                                    </div>
                                </motion.div>
                            ))}
                            
                            {isTyping && (
                                <motion.div
                                    className="message ai typing"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                >
                                    <div className="typing-indicator">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>
                                </motion.div>
                            )}
                            
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Chat Input */}
                        <div className="chat-input">
                            <input
                                ref={inputRef}
                                type="text"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="اكتب سؤالك هنا..."
                                className="chat-input-field"
                            />
                            <motion.button
                                className="send-button"
                                onClick={handleSendMessage}
                                disabled={!inputValue.trim()}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                📤
                            </motion.button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default AIChat;
